import { getFollowerTemplate } from '../../../data/followers';
import type { MoveRef } from '../../../schemas/character';
import {
  type ChoiceGroup,
  getMoveChoiceGroups,
  isNarrativeFieldVisible,
  type Move,
} from '../../../schemas/move';
import { getPickCountForOutcome } from '../../../services/followerCreation';
import { isMoveChoiceFree } from '../sections/moveChoiceConstraints';

/**
 * Verifica se il setup di una mossa (choices/choiceGroups + narrativeFields)
 * è stato compilato correttamente dal giocatore.
 *
 * **FI-18 Fase 2 + FI-32**: unifica i 3 pattern di setup mossa in un unico
 * predicate, e tiene conto delle opzioni `freeForArchetype` (FI-32):
 *  - per ogni `ChoiceGroup` ritornato da {@link getMoveChoiceGroups}, le
 *    selezioni "paid" devono essere esattamente `pickCount`. Le opzioni
 *    `freeForArchetype === archetypeId` non contano nel count (sono bonus
 *    automatici). Per il gruppo virtuale `'main'` (shorthand) le selezioni
 *    vivono in `MoveRef.selectedChoices`; per i gruppi reali in
 *    `MoveRef.selectedChoiceGroups[groupId]`;
 *  - per ogni `NarrativeField` con `required: true`, il valore in
 *    `MoveRef.narrativeFieldValues[fieldId]` deve essere non-vuoto dopo
 *    `trim()`. Anche `.` è accettato (vincolo intenzionalmente lasco).
 *
 * **D8a — recruitRoll**: per mosse-source di Follower con `recruitRoll` sul
 * template (Druido Supporto Elementale, Sciamano Animare Zombi), il
 * `recruitOutcome` deve essere dichiarato (`'10+' | '7-9'`) prima che il
 * setup possa essere completato; il `pickCount` del gruppo `main` viene
 * derivato runtime dall'esito (10+ → onSuccess.choicesCount, 7-9 →
 * onPartial.choicesCount).
 *
 * Ritorna `true` per mosse senza setup strutturato.
 */
export function isMoveSetupComplete(
  move: Move,
  ref: MoveRef,
  archetypeId: string | undefined,
): boolean {
  if (!hasRecruitOutcomeIfRequired(move, ref)) return false;

  for (const group of getMoveChoiceGroups(move)) {
    const selected = pickGroupSelections(ref, group);
    const paidCount = countPaidSelections(group, selected, archetypeId);
    const expectedCount = getEffectivePickCount(move, ref, group);
    if (paidCount !== expectedCount) return false;
  }

  for (const field of move.narrativeFields ?? []) {
    if (!field.required) continue;
    // FI-8a: field con `requiresChoice` non soddisfatto è nascosto dalla UI
    // e quindi non va richiesto al giocatore.
    if (!isNarrativeFieldVisible(field, ref.selectedChoices, ref.selectedChoiceGroups)) continue;
    const value = ref.narrativeFieldValues?.[field.id] ?? '';
    if (value.trim().length < 1) return false;
  }

  return true;
}

/**
 * D8a — verifica che `MoveRef.recruitOutcome` sia presente se la mossa
 * referenzia un `FollowerTemplate` con `recruitRoll`. Per le altre mosse
 * (la maggior parte), ritorna `true` (nessun outcome richiesto).
 */
function hasRecruitOutcomeIfRequired(move: Move, ref: MoveRef): boolean {
  if (!move.followerTemplateId) return true;
  const template = getFollowerTemplate(move.followerTemplateId);
  if (!template?.recruitRoll) return true;
  return ref.recruitOutcome !== undefined;
}

/**
 * D8a — pickCount effettivo del gruppo di scelte. Per il gruppo `main` di
 * una mossa-source di Follower con `recruitRoll`, il pickCount è derivato
 * dall'esito del tiro (`MoveRef.recruitOutcome`). Per gli altri gruppi
 * (e per mosse senza recruitRoll), torna `group.pickCount` statico.
 */
function getEffectivePickCount(move: Move, ref: MoveRef, group: ChoiceGroup): number {
  if (group.id !== 'main' || !move.followerTemplateId) return group.pickCount;
  const template = getFollowerTemplate(move.followerTemplateId);
  if (!template?.recruitRoll) return group.pickCount;
  return getPickCountForOutcome(template, ref.recruitOutcome) ?? group.pickCount;
}

/**
 * Estrae le selezioni per un gruppo specifico dal `MoveRef`, gestendo
 * entrambi i pattern: shorthand single-group (gruppo virtuale `'main'`
 * → `selectedChoices`) e multi-gruppo (`selectedChoiceGroups[g.id]`).
 *
 * **Precedenza** (D8a fix): se `selectedChoiceGroups[group.id]` è popolato,
 * lo usa anche per `group.id === 'main'`. Questo serve a mosse che hanno
 * `choiceGroups` (non shorthand) con un gruppo chiamato `main` — caso reale
 * `druido.supporto-elementale`, dove `main` contiene le peculiarità in un
 * gruppo accanto a `elemento`. Per le mosse `choices` shorthand, il gruppo
 * virtuale `main` non è popolato in `selectedChoiceGroups` e si cade sul
 * fallback `ref.selectedChoices`.
 */
function pickGroupSelections(ref: MoveRef, group: ChoiceGroup): readonly string[] {
  const fromGroups = ref.selectedChoiceGroups?.[group.id];
  if (fromGroups !== undefined) return fromGroups;
  if (group.id === 'main') return ref.selectedChoices;
  return [];
}

/**
 * **FI-32**: conta le selezioni "paid" del gruppo, escludendo le opzioni
 * `freeForArchetype === archetypeId` (bonus automatici, non contano nel
 * `pickCount`).
 */
function countPaidSelections(
  group: ChoiceGroup,
  selected: readonly string[],
  archetypeId: string | undefined,
): number {
  let paid = 0;
  for (const id of selected) {
    const opt = group.options.find((o) => o.id === id);
    if (opt && isMoveChoiceFree(opt, archetypeId)) continue;
    paid += 1;
  }
  return paid;
}
